'use client';

/**
 * ProjectModal Component
 * ======================
 *
 * A modal component that displays detailed information about a Magnate Group project.
 * Triggered when user clicks on a project in the FlowingMenu.
 *
 * HOW IT WORKS:
 * 1. Receives project data (name, image, description, details) as props
 * 2. Renders a glassmorphism modal overlay
 * 3. Displays project image with title and description
 * 4. Includes a close button and click-outside-to-close functionality
 *
 * ANIMATIONS:
 * - Fade in/out for overlay
 * - Scale + fade for modal content
 * - Uses CSS transitions for smooth effects
 */

import React, { useEffect, useCallback } from 'react';
import Image from 'next/image';

export interface ProjectData {
  name: string;
  tagline: string;
  type: string;
  configurations: string[];
  location: string;
  description: string;
  image: string;
  amenities?: string[];
}

interface ProjectModalProps {
  project: ProjectData | null;
  isOpen: boolean;
  onClose: () => void;
}

const ProjectModal: React.FC<ProjectModalProps> = ({ project, isOpen, onClose }) => {
  // Handle ESC key to close modal
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  // Don't render if not open or no project data
  if (!isOpen || !project) return null;

  return (
    <div
      className="project-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="project-modal-title"
    >
      {/* Modal Content - stop propagation to prevent closing when clicking inside */}
      <div
        className="project-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          className="project-modal-close"
          onClick={onClose}
          aria-label="Close modal"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Project Image */}
        <div className="project-modal-image">
          <Image
            src={project.image}
            alt={project.name}
            fill
            className="object-cover"
            sizes="(max-width: 768px) 100vw, 800px"
          />
          {/* Gradient overlay for text readability */}
          <div className="project-modal-image-gradient" />
        </div>

        {/* Project Details */}
        <div className="project-modal-details">
          {/* Header */}
          <div className="project-modal-header">
            <span className="project-modal-type">{project.type}</span>
            <h2 id="project-modal-title" className="project-modal-title">
              {project.name}
            </h2>
            <p className="project-modal-tagline">&ldquo;{project.tagline}&rdquo;</p>
          </div>

          {/* Configurations */}
          <div className="project-modal-configs">
            {project.configurations.map((config) => (
              <span key={config} className="project-modal-config-tag">
                {config}
              </span>
            ))}
          </div>

          {/* Location */}
          <div className="project-modal-location">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <span>{project.location}</span>
          </div>

          {/* Description */}
          <p className="project-modal-description">{project.description}</p>

          {/* Amenities Preview (if available) */}
          {project.amenities && project.amenities.length > 0 && (
            <div className="project-modal-amenities">
              <h3 className="project-modal-amenities-title">Key Amenities</h3>
              <div className="project-modal-amenities-list">
                {project.amenities.slice(0, 6).map((amenity) => (
                  <span key={amenity} className="project-modal-amenity-tag">
                    {amenity}
                  </span>
                ))}
                {project.amenities.length > 6 && (
                  <span className="project-modal-amenity-more">
                    +{project.amenities.length - 6} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;
